/**
 * Resume Candor IDE download after login/registration (pairs with public/candor-website/download-flow.js).
 */
import { apiUrl } from './apiBase';
import { legacyApiPath } from '../config/environments';

export const PENDING_DOWNLOAD_KEY = 'candor_pending_download';

export function getPendingDownload() {
  try {
    const raw = sessionStorage.getItem(PENDING_DOWNLOAD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingDownload() {
  try {
    sessionStorage.removeItem(PENDING_DOWNLOAD_KEY);
  } catch {
    /* noop */
  }
}

export async function resolveDownloadUrl(explicitUrl = null) {
  if (explicitUrl) return explicitUrl;

  const pending = getPendingDownload();
  if (pending?.url) return pending.url;

  // 1. Try manifest first (most reliable for frontend domain matching setup)
  try {
    const res = await fetch(`${window.location.origin}/update_manifest.json`);
    if (res.ok) {
      const manifest = await res.json();
      if (manifest?.initial_download_link) {
        return manifest.initial_download_link;
      }
    }
  } catch (err) {
    /* fallback to next */
  }

  // 2. Try auth endpoint (fallback)
  try {
    const res = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_AUTH_LATEST_DOWNLOAD || legacyApiPath('latest_download')));
    if (res.ok) {
      const data = await res.json();
      if (data?.download_url) {
        return data.download_url;
      }
    }
  } catch (err) {
    /* fallback to next */
  }

  // 3. Try directory listing on "/candor-exe/" using robust DOMParser lookup
  try {
    const res = await fetch(`${window.location.origin}/candor-exe/`);
    if (res.ok) {
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const links = [];
        doc.querySelectorAll('a').forEach(a => {
          const href = a.getAttribute('href');
          if (href && /\.(zip|exe)$/i.test(href)) {
            const decoded = decodeURIComponent(href);
            if (!decoded.startsWith('..') && !decoded.includes('?') && !decoded.includes('//')) {
              links.push(decoded);
            }
          }
        });
        
        if (links.length > 0) {
          const setupLink = links.find(l => l.toLowerCase().includes('setup')) || links[0];
          if (setupLink.startsWith('/')) {
            return setupLink;
          }
          if (setupLink.startsWith('candor-exe/')) {
            return '/' + setupLink;
          }
          return `/candor-exe/${setupLink}`;
        }
      }
    }
  } catch (err) {
    /* fallback below */
  }

  return '/candor-exe/CandorAI-Setup.zip';
}

export function triggerBrowserDownload(url) {
  if (!url) return false;
  const fileName = url.split('/').pop() || 'CandorAI-Setup.zip';
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.target = '_blank';
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  return true;
}

/** After successful auth, start pending IDE download if requested. */
async function trackCompletedDownload(pending, url) {
  try {
    const body = {
      file_name: url.split('/').pop(),
      download_url: url,
      user_email: pending?.email || null,
      first_name: pending?.firstName || null,
      last_name: pending?.lastName || null,
      event_type: 'download_started',
      download_status: 'completed',
      flow_type: 'post_auth',
      user_authenticated: true,
      page_url: window.location.href,
      host_name: window.location.hostname,
    };
    await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_DOWNLOAD_TRACK || '/api/v1/downloads/track'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  } catch {
    /* non-blocking */
  }
}

export async function resumePendingDownloadAfterAuth() {
  const pending = getPendingDownload();
  if (!pending) return false;

  // Only honor download intents we stamped (blocks foreign/stale session storage abuse)
  if (pending.flow && pending.flow !== 'download') {
    clearPendingDownload();
    return false;
  }
  // Max age 30 minutes
  if (pending.at && Date.now() - Number(pending.at) > 30 * 60 * 1000) {
    clearPendingDownload();
    return false;
  }

  let url = await resolveDownloadUrl(pending.url || null);
  // Fail closed: only same-origin relative paths or known public download paths
  if (url) {
    try {
      if (/^https?:\/\//i.test(url)) {
        const u = new URL(url);
        if (u.origin !== window.location.origin) {
          // Allow only known CDN hosts if configured — default fail closed to relative
          url = '/candor-exe/CandorAI-Setup.zip';
        }
      } else if (url.includes('..') || url.startsWith('//')) {
        url = '/candor-exe/CandorAI-Setup.zip';
      }
    } catch {
      url = '/candor-exe/CandorAI-Setup.zip';
    }
  }

  await trackCompletedDownload(pending, url);
  clearPendingDownload();
  triggerBrowserDownload(url);
  return true;
}

export async function trackDirectDownload(user, url, source = 'website') {
  try {
    const email = user?.email || null;
    const firstName = user?.first_name || user?.name?.split(' ')[0] || null;
    const lastName = user?.last_name || user?.name?.split(' ').slice(1).join(' ') || null;
    const company = user?.company || null;
    const body = {
      file_name: url.split('/').pop(),
      download_url: url,
      user_email: email,
      first_name: firstName,
      last_name: lastName,
      company: company,
      event_type: 'download_started',
      download_status: 'completed',
      flow_type: 'authenticated',
      download_source: source,
      user_authenticated: true,
      page_url: window.location.href,
      host_name: window.location.hostname,
    };
    await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_DOWNLOAD_TRACK || '/api/v1/downloads/track'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.warn('Failed to track direct download:', err);
  }
}
